import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Checkbox, notification, Input, InputNumber, Spin, Pagination } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Clock, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  fetchDowntimeLog,
  updateDowntimeLog,
  addDowntimeLogItem,
  deleteDowntimeLogItem,
  updateDsrLogSections
} from '../../redux/dsr/action';
import DownTimeModal from './DownTimeModal';
import './TechnicianLogSection.css';
import './DownTimeSection.css';

const DOWNTIME_TYPE_MAP = { 1: 'Quality', 2: 'Equipment', 3: 'Weather', 4: 'Operations', 5: 'Contractor' };
const getDowntimeTypeName = (id) => DOWNTIME_TYPE_MAP[id] || 'Unknown';

const DownTimeSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSkipped,
  isSubmitted,
  onComplete,
  onCancel,
  isShiftSkipped = false,
  onSkipChange,
  onFetchDowntimeLog,
  onUpdateDowntimeLog,
  onAddDowntimeLogItem,
  onDeleteDowntimeLogItem,
  onUpdateDsrLogSections,
  isDowntimeLogLoading,
  isUpdatingDowntimeLog,
  currentDowntimeLog,
}) => {
  const [rowData, setRowData] = useState([]);
  const [skipped, setSkipped] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [paginationState, setPaginationState] = useState({ current: 1, pageSize: 10 });

  const getShiftNumber = (shiftStr) => (shiftStr === 'day' ? 0 : 1);

  useEffect(() => {
    if (selectedProject && selectedMainline && selectedDate && !isSkipped) {
      const params = {
        projectId: selectedProject.id,
        logDate: moment(selectedDate).format('YYYY-MM-DD'),
        userEmail: JSON.parse(localStorage.getItem('user')).email,
        shift: getShiftNumber(shift),
        sowId: selectedMainline.id,
      };
      onFetchDowntimeLog(params);
    }
  }, [selectedProject, selectedMainline, selectedDate, shift, isSkipped, onFetchDowntimeLog]);

  useEffect(() => {
    if (currentDowntimeLog?.data) {
      const tableData = currentDowntimeLog.data.map((item) => ({
        ...item,
        downtimeTypeName: getDowntimeTypeName(item.downtimeType),
      }));
      setRowData(tableData);
      setIsDirty(false);
      setSelectedRowKeys([]);
    }
  }, [currentDowntimeLog]);

  useEffect(() => {
    setIsDirty(false);
  }, [selectedDate]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  useEffect(() => {
    return () => {
      setRowData([]);
      setSkipped(false);
      setIsDirty(false);
      setSelectedRowKeys([]);
    };
  }, []);

  const updateRowField = useCallback((id, field, value) => {
    setIsDirty(true);
    setRowData((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  }, []);

  const totalRows = rowData.length;
  const { current: currentPage, pageSize } = paginationState;
  const maxPage = Math.max(1, totalRows === 0 ? 1 : Math.ceil(totalRows / pageSize));
  const page = Math.min(currentPage, maxPage);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rowData.slice(start, start + pageSize);
  }, [rowData, page, pageSize]);

  useEffect(() => {
    if (currentPage > maxPage) {
      setPaginationState((s) => ({ ...s, current: maxPage }));
    }
  }, [currentPage, maxPage]);

  const isCellDisabled = skipped || isSubmitted;

  const pagedRowIds = useMemo(() => new Set(pagedRows.map((r) => r.id)), [pagedRows]);
  const selectedInPage = selectedRowKeys.filter((id) => pagedRowIds.has(id));
  const allInPageSelected = pagedRows.length > 0 && selectedInPage.length === pagedRows.length;
  const someInPageSelected = selectedInPage.length > 0 && !allInPageSelected;

  const toggleSelectAllInPage = () => {
    if (isCellDisabled) return;
    if (allInPageSelected) {
      setSelectedRowKeys((prev) => prev.filter((id) => !pagedRowIds.has(id)));
    } else {
      setSelectedRowKeys((prev) => {
        const next = new Set([...prev, ...pagedRows.map((r) => r.id)]);
        return Array.from(next);
      });
    }
  };

  const handleDeleteSelected = () => {
    const selectedIds = [...selectedRowKeys];
    selectedIds.forEach((id) => {
      onDeleteDowntimeLogItem({
        id,
        projectId: selectedProject.id,
        logDate: moment(selectedDate).format('YYYY-MM-DD'),
        userEmail: JSON.parse(localStorage.getItem('user')).email,
        shift: getShiftNumber(shift),
        sowId: selectedMainline.id,
      });
    });
    setSelectedRowKeys([]);
  };

  const generateSubmissionStatus = (section, sh, isSubmittedValue = true) => {
    const shiftKeySuffix = sh === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;

    switch (section) {
      case 'technician':
        submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
        break;
      case 'downtime':
        submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
        break;
      case 'consumables':
        submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
        break;
      case 'equipment':
        submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'ehs':
        submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
        break;
      case 'material':
        submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'production':
        submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
        break;
      default:
        return {};
    }

    return { [submittedKey]: isSubmittedValue };
  };

  const handleAddEntry = (values) => {
    onAddDowntimeLogItem({
      dsrLogDowntime: values,
      shift: getShiftNumber(shift),
      projectId: selectedProject.id,
      logDate: moment(selectedDate).format('YYYY-MM-DD'),
      userEmail: JSON.parse(localStorage.getItem('user')).email,
      sowId: selectedMainline.id,
    });
    setIsModalVisible(false);
  };

  const handleSave = async () => {
    if (skipped) {
      if (onComplete) onComplete(true, 'downtime');
      return;
    }

    try {
      const updatedData = rowData;
      const payload = {
        dsrLogDowntime: updatedData,
        projectId: selectedProject.id,
        logDate: moment(selectedDate).format('YYYY-MM-DD'),
        userEmail: JSON.parse(localStorage.getItem('user')).email,
        shift: getShiftNumber(shift),
        sowId: selectedMainline.id,
      };

      await onUpdateDowntimeLog(payload);
      setIsDirty(false);

      const submissionStatus = generateSubmissionStatus('downtime', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isDowntimeSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isDowntimeSkippedDayShift || 0),
        isDowntimeSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isDowntimeSkippedNightShift || 0)
      };

      await onUpdateDsrLogSections({
        dsrLogId: currentDsrLog?.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped
      });

      if (onComplete) onComplete(true, 'downtime');

      if (selectedProject && selectedMainline && selectedDate) {
        const userEmail = JSON.parse(localStorage.getItem('user')).email;
        const logDate = moment(selectedDate).format('YYYY-MM-DD');
        const shiftNumber = shift === 'day' ? '0' : '1';

        onFetchDowntimeLog({
          projectId: selectedProject.id,
          logDate: logDate,
          userEmail: userEmail,
          shift: shiftNumber,
          sowId: selectedMainline.id
        });
      }
    } catch (error) {
      console.error('Error saving downtime log:', error);
      notification.error({
        message: 'Save Failed',
        description: 'Failed to save downtime data. Please try again.'
      });
    }
  };

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);
    setIsDirty(true);

    if (onSkipChange) {
      onSkipChange(e);
    } else if (currentDsrLog?.id) {
      const submissionStatus = generateSubmissionStatus('downtime', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isDowntimeSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isDowntimeSkippedDayShift || 0),
        isDowntimeSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isDowntimeSkippedNightShift || 0),
      };

      onUpdateDsrLogSections({
        dsrLogId: currentDsrLog.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped
      });
    }
  };

  return (
    <div className="modern-dsr-tech-log-section modern-dsr-dt-usage">
      {isShiftSkipped ? (
        <div className="modern-dsr-tech-log-skipped-banner">
          <strong>
            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
          </strong>
          <p>
            This section is disabled because the {shift} shift has been skipped.
          </p>
        </div>
      ) : (
        <Spin
          spinning={isDowntimeLogLoading}
          indicator={(
            <span className="modern-dsr-tech-log-spin" aria-hidden>
              <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
            </span>
          )}
          tip="Loading downtime data..."
          size="large"
        >
          <div className="modern-dsr-tech-log-card">
            <div className="modern-dsr-tech-log-card-head modern-dsr-dt-head">
              <div className="modern-dsr-tech-log-card-title-block">
                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                  <Clock size={22} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="modern-dsr-tech-log-card-title">Downtime</h3>
                  <p className="modern-dsr-tech-log-card-sub">Log downtime by type, equipment, and minutes for this shift.</p>
                </div>
              </div>
              <div className="modern-dsr-dt-toolbar" role="group" aria-label="Downtime row actions">
                <button
                  type="button"
                  className="modern-dsr-dt-icon-btn"
                  onClick={() => setIsModalVisible(true)}
                  disabled={isCellDisabled}
                  aria-label="Add downtime entry"
                >
                  <Plus size={18} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="modern-dsr-dt-icon-btn modern-dsr-dt-icon-btn--danger"
                  onClick={handleDeleteSelected}
                  disabled={isCellDisabled || selectedRowKeys.length === 0}
                  aria-label="Delete selected entries"
                >
                  <Trash2 size={18} strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className="modern-dsr-tech-table" role="table" aria-label="Downtime">
              <div className="modern-dsr-tech-thead modern-dsr-dt-grid" role="row">
                <div className="modern-dsr-tech-th modern-dsr-dt-th--check" role="columnheader" aria-label="Select rows">
                  <Checkbox
                    checked={allInPageSelected}
                    indeterminate={someInPageSelected}
                    onChange={toggleSelectAllInPage}
                    disabled={pagedRows.length === 0 || isCellDisabled}
                  />
                </div>
                <div className="modern-dsr-tech-th" role="columnheader">Type</div>
                <div className="modern-dsr-tech-th" role="columnheader">Equipment</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Coating downtime (min.)</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Contractor downtime (min.)</div>
                <div className="modern-dsr-tech-th" role="columnheader">Comments</div>
                <div className="modern-dsr-tech-th" role="columnheader">Issue</div>
                <div className="modern-dsr-tech-th" role="columnheader">Action taken</div>
              </div>

              {pagedRows.length === 0 ? (
                <div className="modern-dsr-tech-empty">No downtime lines loaded.</div>
              ) : (
                pagedRows.map((row) => (
                  <div
                    key={row.id}
                    className="modern-dsr-tech-row modern-dsr-dt-grid"
                    role="row"
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    data-active={hoveredRowId === row.id ? 'true' : undefined}
                  >
                    <div className="modern-dsr-tech-td modern-dsr-dt-td--check" role="cell">
                      <Checkbox
                        checked={selectedRowKeys.includes(row.id)}
                        onChange={() => {
                          if (isCellDisabled) return;
                          setSelectedRowKeys((prev) => (
                            prev.includes(row.id)
                              ? prev.filter((k) => k !== row.id)
                              : [...prev, row.id]
                          ));
                        }}
                        disabled={isCellDisabled}
                        aria-label={`Select row ${row.pihEquipmentType || row.id}`}
                      />
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-dt-td--readonly" role="cell" title={row.downtimeTypeName}>
                      <span className="modern-dsr-tech-name-text">{row.downtimeTypeName}</span>
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-dt-td--readonly" role="cell" title={row.pihEquipmentType}>
                      <span className="modern-dsr-tech-name-text">{row.pihEquipmentType}</span>
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-dt-td--num" role="cell">
                      <div className="modern-dsr-tech-hours-wrap modern-dsr-dt-num-wrap">
                        <InputNumber
                          className="modern-dsr-tech-inputnum"
                          min={0}
                          value={row.coatingDowntime}
                          onChange={(v) => updateRowField(row.id, 'coatingDowntime', v == null ? null : v)}
                          disabled={isCellDisabled}
                        />
                      </div>
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-dt-td--num" role="cell">
                      <div className="modern-dsr-tech-hours-wrap modern-dsr-dt-num-wrap">
                        <InputNumber
                          className="modern-dsr-tech-inputnum"
                          min={0}
                          value={row.contractorDowntime}
                          onChange={(v) => updateRowField(row.id, 'contractorDowntime', v == null ? null : v)}
                          disabled={isCellDisabled}
                        />
                      </div>
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <Input
                        className="modern-dsr-dt-text-input"
                        value={row.comments ?? ''}
                        onChange={(e) => updateRowField(row.id, 'comments', e.target.value)}
                        disabled={isCellDisabled}
                        placeholder="Comments"
                      />
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <Input
                        className="modern-dsr-dt-text-input"
                        value={row.issues ?? ''}
                        onChange={(e) => updateRowField(row.id, 'issues', e.target.value)}
                        disabled={isCellDisabled}
                        placeholder="Issue"
                      />
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <Input
                        className="modern-dsr-dt-text-input"
                        value={row.actionTaken ?? ''}
                        onChange={(e) => updateRowField(row.id, 'actionTaken', e.target.value)}
                        disabled={isCellDisabled}
                        placeholder="Action taken"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modern-dsr-tech-log-pagination-bar">
              <Pagination
                className="heat-profile-pagination modern-dsr-tech-heat-profile-pagination"
                current={page}
                pageSize={pageSize}
                total={totalRows}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                showTotal={(t, range) =>
                  t > 0 ? `${range[0]}-${range[1]} of ${t}` : `0-0 of ${t}`
                }
                onChange={(p, newSize) => {
                  setPaginationState((s) => {
                    if (newSize !== s.pageSize) {
                      return { current: 1, pageSize: newSize };
                    }
                    return { ...s, current: p };
                  });
                }}
              />
            </div>
          </div>
        </Spin>
      )}

      <div className="modern-dsr-tech-log-form-footer" aria-label="Form actions">
        <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
        <CrceButton
          label={isUpdatingDowntimeLog ? 'Saving…' : 'Save & Next'}
          variant="primary"
          size="medium"
          icon={Save}
          onClick={handleSave}
          disabled={isUpdatingDowntimeLog || isSubmitted || isShiftSkipped}
        />
      </div>

      <DownTimeModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleAddEntry}
        projectId={selectedProject?.id}
      />
    </div>
  );
};

const mapStateToProps = ({ dsrReducer }) => ({
  isDowntimeLogLoading: dsrReducer.isDowntimeLogLoading,
  isUpdatingDowntimeLog: dsrReducer.isUpdatingDowntimeLog,
  currentDowntimeLog: dsrReducer.currentDowntimeLog,
});

const mapDispatchToProps = {
  onFetchDowntimeLog: fetchDowntimeLog,
  onUpdateDowntimeLog: updateDowntimeLog,
  onAddDowntimeLogItem: addDowntimeLogItem,
  onDeleteDowntimeLogItem: deleteDowntimeLogItem,
  onUpdateDsrLogSections: updateDsrLogSections
};

export default connect(mapStateToProps, mapDispatchToProps)(DownTimeSection);
